<template>
  <div class="line-chart">
    <div v-if="lineType === 'single'" class="legend-container">
      <div class="legend">{{ legendTitle }}</div>
    </div>
    <div v-else class="muti-legend-container">
      <div class="legend-item">
        <div class="legend">当期{{ legendTitle }}</div>
        <div class="date">{{ chartData.curDate }}</div>
      </div>
      <div class="legend-item">
        <div class="legend pink">上期{{ legendTitle }}</div>
        <div class="date">{{ chartData.preDate }}</div>
      </div>
    </div>
    <div class="chart" ref="chart" :style="{ height: heightToRem, width: width }" />
  </div>
</template>

<script>
const echarts = require('echarts/lib/echarts')
require('echarts/lib/chart/line') //折线图
require('echarts/lib/chart/pie') //饼图
//下面的是需要的提示框
require('echarts/lib/component/tooltip')
require('echarts/lib/component/title')
require('echarts/lib/component/legend')
require('echarts/theme/macarons') // echarts theme

import { debounce } from '@/utils'
import { typeInfo } from '../utils/dataType'
import { getDateStrAfter } from '@/utils/index.js'
export default {
  props: {
    width: {
      type: String,
      default: '105%'
    },
    height: {
      type: Number,
      default: 300
    },
    autoResize: {
      type: Boolean,
      default: true
    },
    chartData: {
      type: Object,
      required: true
    },
    lineType: {
      type: String,
      default: 'single'
    }
  },
  data() {
    return {
      chart: null
    }
  },
  computed: {
    heightToRem() {
      const radio = 37.5
      return this.height / 37.5 + 'rem'
    },
    legendTitle() {
      const { type, cardType } = this.chartData
      if (!type || !cardType) return ''
      return typeInfo[type][cardType]
    }
  },
  watch: {
    chartData: {
      deep: true,
      handler(val) {
        this.setOptions(val)
      }
    }
  },
  mounted() {
    this.initChart()
    if (this.autoResize) {
      this.__resizeHandler = debounce(() => {
        if (this.chart) {
          this.chart.resize()
        }
      }, 100)
      window.addEventListener('resize', this.__resizeHandler)
    }
  },
  beforeDestroy() {
    if (!this.chart) {
      return
    }
    if (this.autoResize) {
      window.removeEventListener('resize', this.__resizeHandler)
    }

    this.chart.dispose()
    this.chart = null
  },
  methods: {
    setOptions(chartData) {
      const self = this
      if (self.lineType === 'mutiple') {
        self.setMutipleOptions(chartData)
      } else {
        self.setSingleOptions(chartData)
      }
      self.chart.getZr().on('mouseup', function(e) {
        self.chart.dispatchAction({
          type: 'hideTip' //隐藏tooltip
        })
        self.chart.dispatchAction({
          type: 'updateAxisPointer',
          currTrigger: 'leave' //隐藏竖线指示器
        })
      })
    },
    setSingleOptions(chartData) {
      if (!chartData.type || !chartData.cardType || !chartData.data) {
        return
      }
      const xData = chartData.data.x
      const yData = chartData.data.y
      const label = typeInfo[chartData.type][chartData.cardType]
      const showRMB = label === '会员销售额' || label === '复购销售额'
      const showPercent = label === '复购率'
      const fontSize = 10
      this.chart.setOption({
        grid: {
          left: '1%',
          right: '3%',
          bottom: '5%',
          top: '5%',
          containLabel: true
        },
        tooltip: {
          trigger: 'axis',
          extraCssText: 'box-shadow:0px 0px 8px 0px rgba(0,0,0,0.14);',
          backgroundColor: '#fff',
          textStyle: {
            color: '#444444'
          },
          axisPointer: {
            lineStyle: {
              type: 'dashed',
              color: '#888888'
            }
          },
          padding: [5, 10],
          confine: true,
          formatter: function(datas) {
            if (showRMB) {
              return `<div style="font-size: ${fontSize}px;color: #999999;">${datas[0].axisValue} <br/></div><div style="font-size: ${fontSize}px;">${datas[0].marker}￥${datas[0].value}</div>`
            } else if (showPercent) {
              return `<div style="font-size: ${fontSize}px;color: #999999;">${datas[0].axisValue} <br/></div><div style="font-size: ${fontSize}px;"> ${datas[0].marker}${datas[0].value}%</div>`
            }
            return `<div style="font-size: ${fontSize}px;color: #999999;">${datas[0].axisValue} <br/></div><div style="font-size: ${fontSize}px;"> ${datas[0].marker}${datas[0].value}</div>`
          }
        },
        xAxis: {
          type: 'category',
          boundaryGap: true,
          data: xData,
          axisLine: {
            lineStyle: {
              color: 'rgba(187,187,187,1)', // 颜色
              width: 1 // 粗细
            }
          },
          axisTick: {
            alignWithLabel: true
          },
          axisLabel: {
            show: true,
            textStyle: {
              color: '#555555', // 更改坐标轴文字颜色
              fontSize: fontSize // 更改坐标轴文字大小
            },
            interval: 60,
            showMinLabel: true,
            showMaxLabel: true,
            formatter: function(value, index) {
              if (chartData.type === 'member' || chartData.type === 'repurchase') {
                return value.substr(5)
              } else {
                return value
              }
            }
          }
        },
        yAxis: {
          splitArea: {
            show: false
          },
          type: 'value',
          axisLine: {
            lineStyle: {
              color: 'rgba(187,187,187,1)', // 颜色
              width: 0 // 粗细
            }
          },
          axisLabel: {
            show: true,
            textStyle: {
              color: '#555555', // 更改坐标轴文字颜色
              fontSize: fontSize // 更改坐标轴文字大小
            },
            formatter: function(value, index) {
              if (showRMB) {
                return `￥${value}`
              } else if (showPercent) {
                return `${value}%`
              } else {
                return value
              }
            }
          }
        },
        series: [
          {
            name: label,
            itemStyle: {
              normal: {
                color: '#2196F3',
                lineStyle: {
                  color: '#2196F3',
                  width: 0.5
                }
              }
            },
            smooth: false,
            type: 'line',
            data: yData,
            //symbol: 'circle',
            symbol: 'none',
            symbolSize: 3
          }
        ]
      })
    },
    setMutipleOptions(chartData) {
      const _self = this
      if (!chartData.type || !chartData.cardType || !chartData.data) {
        return
      }
      const xData = chartData.data.x
      const y1Data = chartData.data.y1
      const y2Data = chartData.data.y2
      const dateInterval = chartData.data.days + 1
      const label = typeInfo[chartData.type][chartData.cardType]
      const showRMB = label === '下单金额'
      const label1 = `当期${label}`
      const label2 = `上期${label}`
      const fontSize = 10
      this.chart.setOption({
        grid: {
          left: '1%',
          right: '5%',
          bottom: '5%',
          top: '5%',
          containLabel: true
        },
        tooltip: {
          trigger: 'axis',
          extraCssText: 'box-shadow:0px 0px 8px 0px rgba(0,0,0,0.14);',
          backgroundColor: '#fff',
          textStyle: {
            color: '#444444'
          },
          axisPointer: {
            lineStyle: {
              type: 'dashed',
              color: '#888888'
            }
          },
          confine: true,
          padding: [5, 10],
          formatter: function(datas) {
            const curDateStr = getDateStrAfter(-dateInterval, new Date(datas[0].axisValue))
            if (showRMB) {
              return `<div style="font-size: ${fontSize}px;color: #999999;">${datas[0].axisValue} <br/></div><div style="font-size: ${fontSize}px;">${datas[0].marker}￥${datas[0].value}</div><hr/><div style="font-size: ${fontSize}px;color: #999999;">${curDateStr} <br/></div><div style="font-size: ${fontSize}px;">${datas[1].marker}￥${datas[1].value}</div>`
            }
            return `<div style="font-size: ${fontSize}px;color: #999999;">${datas[0].axisValue} <br/></div><div style="font-size: ${fontSize}px;"> ${datas[0].marker}${datas[0].value}</div>`
          }
        },
        xAxis: {
          type: 'category',
          boundaryGap: true,
          data: xData,
          axisLine: {
            lineStyle: {
              color: 'rgba(187,187,187,1)', // 颜色
              width: 1 // 粗细
            }
          },
          axisTick: {
            alignWithLabel: true
          },
          axisLabel: {
            show: true,
            textStyle: {
              color: '#555555', // 更改坐标轴文字颜色
              fontSize: fontSize // 更改坐标轴文字大小
            },
            interval: 60,
            showMinLabel: true,
            showMaxLabel: true,
            formatter: function(value, index) {
              if (index === dateInterval - 1) {
                // 解决最后坐标轴文字显示不全问题
                return value + '          '
              }
              return value
            }
          }
        },
        yAxis: {
          splitArea: {
            show: false
          },
          type: 'value',
          axisLine: {
            lineStyle: {
              color: 'rgba(187,187,187,1)', // 颜色
              width: 0 // 粗细
            }
          },
          axisLabel: {
            show: true,
            textStyle: {
              color: '#555555', // 更改坐标轴文字颜色
              fontSize: fontSize // 更改坐标轴文字大小
            },
            formatter: function(value, index) {
              if (showRMB) {
                return `￥${value}`
              } else {
                return value
              }
            }
          }
        },
        series: [
          {
            name: label1,
            itemStyle: {
              normal: {
                color: '#408AFD',
                lineStyle: {
                  color: '#408AFD',
                  width: 0.5
                }
              }
            },
            smooth: false,
            type: 'line',
            data: y1Data,
            symbol: 'none'
            //symbolSize: 3
          },
          {
            name: label2,
            itemStyle: {
              normal: {
                color: '#F1A7B0',
                lineStyle: {
                  color: '#F1A7B0',
                  width: 0.5
                }
              }
            },
            smooth: false,
            type: 'line',
            data: y2Data,
            symbol: 'none'
            //symbolSize: 3
          }
        ]
      })
    },
    initChart() {
      this.chart = echarts.init(this.$refs.chart, 'macarons')
      this.setOptions(this.chartData)
    }
  }
}
</script>
<style lang="scss" scoped>
.line-chart {
  .legend-container {
    font-family: PingFangSC-Regular;
    font-size: 12px;
    color: #666666;
    display: flex;
    justify-content: center;
  }
  .legend {
    position: relative;
    &::before {
      content: '';
      position: absolute;
      top: 4px;
      left: -12px;
      width: 8px;
      height: 8px;
      background: #408afd;
      border-radius: 4px;
    }
    &.pink::before {
      background: #f1a7b0;
    }
  }
  .muti-legend-container {
    font-family: PingFangSC-Regular;
    font-size: 12px;
    color: #666666;
    display: flex;
    justify-content: space-around;
    .date {
      margin-left: -13px;
      margin-top: 4px;
    }
  }
  .chart {
    margin-top: 10px;
    margin-left: -5px;
  }
}
</style>
